import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errText, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const backgroundUrl = (prefs) =>
  prefs?.background_path
    ? `${API}/appearance/background?v=${prefs.background_version || 0}`
    : null;

export const BackgroundPicture = () => {
  const { user, setPreferences } = useAuth();
  const prefs = user?.preferences || {};
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const url = backgroundUrl(prefs);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/appearance/background", form);
      setPreferences(data);
      toast.success("Your picture is now behind the diary");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/appearance/background");
      setPreferences(data);
      toast.success("Picture removed — back to the texture");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="background-picture-setting">
      <Label className="label-caps">Your own picture</Label>
      <div className="flex flex-wrap items-center gap-4">
        <div
          data-testid="background-preview"
          className="h-24 w-40 shrink-0 border border-border rounded-sm bg-secondary bg-cover bg-center grid place-items-center"
          style={url ? { backgroundImage: `url(${url})` } : undefined}
        >
          {url ? null : (
            <span className="text-xs text-muted-foreground">No picture</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            data-testid="background-file-input"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
          />
          <Button
            variant="outline"
            data-testid="background-choose-btn"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="rounded-full h-10 px-4 text-sm"
          >
            <ImagePlus className="h-4 w-4 mr-2" />
            {busy ? "Working…" : url ? "Change picture" : "Choose a picture"}
          </Button>
          {url ? (
            <Button
              variant="outline"
              data-testid="background-remove-btn"
              disabled={busy}
              onClick={remove}
              className="rounded-full h-10 px-4 text-sm text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Remove
            </Button>
          ) : null}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        PNG, JPEG or WEBP up to 8 MB. It sits behind every page — the strength slider below controls
        how strongly it shows. Only you see it.
      </p>
    </div>
  );
};
